import React, {Component} from 'react';
import {Grid, Icon} from 'semantic-ui-react';
import {Alert} from 'componentLibrary';
import _ from 'lodash';
import {FemaGoTable} from 'app-ui-componentlibrary';
import {convertDateForDisplay, convertUTCDateToLocalDateDisplay} from 'common/utils';
import {
  FileTransferUtils,
  convertFileSize,
  convertMaxFileSize,
} from 'go/components/common/attachments/uploader/utils';
import {DeleteModal} from 'app-ui-componentlibrary';
import ReactHtmlParser from 'react-html-parser';
import REST from 'go/requests/rest';
import {safeMult} from 'common/utils';
import {downloadPresignedLink, usePresignedUrl} from 'common/utils';
import {produce} from 'immer';
import {produceAppliedDiff} from 'framework/collaboration/diff.utils';

export class UploadPresentational extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      filesSuccessful: this.props.filesSuccessful,
      deleteItemModalOpen: false,
    };
  }
  showSingleAttachments = true;

  openDeleteModal = () => this.setState({deleteItemModalOpen: true});
  closeDeleteModal = () => this.setState({deleteItemModalOpen: false});

  setDeleteItem = (id) => {
    this.setState({
      deleteItemID: id,
    });
    this.openDeleteModal();
  };

  confirmDeleteItem = () => {
    this.closeDeleteModal();
    this.props.handleDelete(this.state.deleteItemID);
  };

  resetDeleteItem = () => {
    this.setState({
      deleteItemID: null,
    });
    this.closeDeleteModal();
  };

  renderDeleteModal = (displayTextp) => {
    return (
      <DeleteModal
        displayTitle={'Remove Attachment'}
        open={this.state.deleteItemModalOpen}
        cancelAction={this.resetDeleteItem}
        deleteAction={this.confirmDeleteItem}
        displayText={displayTextp}
      />
    );
  };

  componentDidUpdate(prevProps) {
    let {currentlyCollaborating, sortBy, sortOrder} = this.props;
    if (!_.isEqual(prevProps.filesSuccessful, this.props.filesSuccessful)) {
      if (currentlyCollaborating) {
        let filesUpdated = produceAppliedDiff(
          prevProps.filesSuccessful,
          this.props.filesSuccessful,
          this.state.filesSuccessful
        );
        sortBy = sortBy || 'uploadedDate';
        sortOrder = sortOrder || 'asc';
        filesUpdated = _.orderBy(filesUpdated, sortBy, sortOrder);
        this.setState((prevState) => ({
          filesSuccessful: filesUpdated,
        }));
      } else {
        this.setState({filesSuccessful: this.props.filesSuccessful});
      }
    }
  }

  downloadAttachmentLink = (resourceId, fileName) => {
    if (usePresignedUrl()) {
      return (
        <a
          href="#"
          onClick={() => {
            downloadPresignedLink(resourceId);
          }}
          title={`Download ${fileName}`}
        >
          {fileName}
        </a>
      );
    } else {
      const downloadUrl = REST.attachment.getAttachmentDownloadUrl(resourceId);
      return (
        <a href={downloadUrl} title={`Download ${fileName}`}>
          {fileName}
        </a>
      );
    }
  };

  renderErrors(messages) {
    return (
      <React.Fragment>
        {!!messages.length && (
          <Alert
            header="There was an error uploading the following attachment:"
            type="error"
          >
            {_.map(messages, (message, index) => (
              <p key={index}>
                <strong>{message.name}: </strong>
                {message.error}
              </p>
            ))}
          </Alert>
        )}
      </React.Fragment>
    );
  }

  renderFailed(files) {
    const hasErrors = _.filter(files, (file) => file.error !== undefined);
    const errorMessages = _.map(hasErrors, (file) => ({
      name: file.fileName,
      error: file.error,
    }));
    return (
      <Grid.Row>
        <Grid.Column width={16}>
          <h4>Failed to upload:</h4>
          {this.renderErrors(errorMessages)}
        </Grid.Column>
      </Grid.Row>
    );
  }

  renderFileInput(noFileDesc) {
    const {
      acceptedFileTypes,
      chooseMulti,
      attachmentType,
      extended_attachment,
      currentlyCollaborating,
    } = this.props;
    const randomKey = Math.random();
    const {filesSuccessful} = currentlyCollaborating ? this.state : this.props;
    const renderAttachmentButton = noFileDesc ? (
      extended_attachment.hideUploadOnSuccess &&
      filesSuccessful.length === extended_attachment.fileLimitsToHide ? null : (
        <div>
          {extended_attachment.showContents &&
            ReactHtmlParser(extended_attachment.templateToDownload)}
          <label
            htmlFor={`attachmentUploadInput-${attachmentType}-${randomKey}`}
            title="Attach a document"
            className={`ui medium primary left floated button ${
              this.props.disabled ? 'disabled' : ''
            }`}
          >
            <Icon name="upload" />
            Upload document
          </label>
        </div>
      )
    ) : (
      <label
        htmlFor={`attachmentUploadInput-${attachmentType}-${randomKey}`}
        title="Attach a document"
        className={`ui medium primary left floated button  ${
          this.props.disabled ? 'disabled' : ''
        }`}
      >
        <Icon name="upload" />
        {extended_attachment && extended_attachment.buttonText
          ? extended_attachment.buttonText
          : 'Attach a document'}
      </label>
    );

    return (
      <React.Fragment>
        <input
          type="file"
          onChange={(e) => {
            e.preventDefault();
            this.props.handleChange(e.target.files);
            e.target.value = '';
          }}
          disabled={this.props.disabled}
          className="attachment-input-file"
          id={`attachmentUploadInput-${attachmentType}-${randomKey}`}
          title="Attach a document"
          multiple={chooseMulti}
          accept={acceptedFileTypes}
        />
        {renderAttachmentButton}
      </React.Fragment>
    );
  }

  handleDescriptionChange = (index, res) => {
    const {name, value} = res;

    if (this.props.currentlyCollaborating) {
      this.setState((prevState) => ({
        filesSuccessful: produce(prevState.filesSuccessful, (draft) => {
          draft[index][name] = value;
        }),
      }));
    } else {
      let filesSuccessful = this.state.filesSuccessful;
      filesSuccessful[index][name] = value;
      this.setState({
        filesSuccessful,
      });
    }
  };

  handleFileSize(fileSize) {
    return convertFileSize(fileSize, 1024);
  }

  formatUniqueID = (attachmentType) => {
    if (_.includes(attachmentType, '.attachmentIds')) {
      return _.capitalize(_.lowerCase(_.replace(attachmentType, '.attachmentIds', '')));
    } else {
      return attachmentType;
    }
  };

  render() {
    const {
      filesFailed,
      attachmentType,
      errorMessages,
      extended_attachment,
      acceptedFileTypes,
      showAcceptedFileTypes,
      hideUploadedBy,
      currentlyCollaborating,
    } = this.props;
    const {filesSuccessful} = currentlyCollaborating ? this.state : this.props;
    const noFileDesc = _.get(extended_attachment, 'noFileDesc', false);
    const acceptedTypesDescriptor = acceptedFileTypes.join(', ');
    return (
      <div>
        {noFileDesc ? (
          <React.Fragment>
            {this.renderDeleteModal('Are you sure you want to delete?')}
            <Grid
              className={`${!_.isEmpty(errorMessages) ? ' errorBorderLine' : ''}`}
              id={`${attachmentType}-section`}
            >
              <Grid.Row>
                <Grid.Column width={16}>
                  {this.renderFileInput(noFileDesc)}
                  <FemaGoTable type="static" basic="very" id={`${attachmentType}-table`}>
                    <FemaGoTable.Body>
                      {_.map(filesSuccessful, (file, index) => {
                        const {
                          fileName,
                          uploadedDate,
                          resourceId,
                          id: attachmentId,
                        } = file;
                        const removeAttachmentLink = (
                          <a
                            id={file.fileName + index + '.removeAction'}
                            className="remove-attachment"
                            onClick={() => this.setDeleteItem(resourceId)}
                            title={`Remove the ${fileName} attachment`}
                            href="#"
                          >
                            <Icon link name="close" color="blue" />
                            Delete
                          </a>
                        );

                        const date = convertDateForDisplay(
                          new Date(uploadedDate * 1000),
                          true
                        );
                        return (
                          <FemaGoTable.Row
                            key={currentlyCollaborating ? attachmentId : index}
                          >
                            <FemaGoTable.TextCell width={3}>
                              <Icon name="check circle" color="green" />
                              {extended_attachment.showContents &&
                                this.downloadAttachmentLink(resourceId, fileName)}{' '}
                              {extended_attachment.showContents && removeAttachmentLink}
                            </FemaGoTable.TextCell>
                          </FemaGoTable.Row>
                        );
                      })}
                    </FemaGoTable.Body>
                  </FemaGoTable>
                </Grid.Column>
              </Grid.Row>
              {!!filesFailed.length && this.renderFailed(filesFailed)}
              {!_.isEmpty(errorMessages) && (
                <div className="errorMessages">{errorMessages}</div>
              )}
              <Grid.Row>
                <Grid.Column />
              </Grid.Row>
            </Grid>
          </React.Fragment>
        ) : (
          <React.Fragment>
            {this.renderDeleteModal('Are you sure you want to remove this attachment?')}
            <Grid
              className={`${!_.isEmpty(errorMessages) ? ' errorBorderLine' : ''}`}
              id={`${attachmentType}-section`}
            >
              <Grid.Row>
                <Grid.Column width={!(extended_attachment && noFileDesc) ? 4 : null}>
                  {this.renderFileInput(noFileDesc)}
                </Grid.Column>
              </Grid.Row>
              {!!filesFailed.length && this.renderFailed(filesFailed)}
              {!_.isEmpty(errorMessages) && (
                <div className="errorMessages">{errorMessages}</div>
              )}
              {!(extended_attachment && noFileDesc) && (
                <Grid.Row>
                  <Grid.Column>
                    <FemaGoTable
                      type="static"
                      className="sortable"
                      id={`${attachmentType}-table`}
                      summary={`Review attachments table-${this.formatUniqueID(
                        attachmentType
                      )}`}
                    >
                      <FemaGoTable.Header>
                        <FemaGoTable.Row>
                          <FemaGoTable.HeaderCell>Filename</FemaGoTable.HeaderCell>
                          <FemaGoTable.HeaderCell>Date uploaded</FemaGoTable.HeaderCell>
                          {!hideUploadedBy ? (
                            <FemaGoTable.HeaderCell>Uploaded by</FemaGoTable.HeaderCell>
                          ) : (
                            ''
                          )}
                          <FemaGoTable.HeaderCell>File size</FemaGoTable.HeaderCell>
                          <FemaGoTable.HeaderCell>Description</FemaGoTable.HeaderCell>
                          <FemaGoTable.HeaderCell>Action</FemaGoTable.HeaderCell>
                        </FemaGoTable.Row>
                      </FemaGoTable.Header>
                      <FemaGoTable.Body>
                        {_.map(filesSuccessful, (file, index) => {
                          const {
                            fileName,
                            uploadedDate,
                            resourceId,
                            description,
                            uploadedBy,
                            fileSize,
                            id: attachmentId,
                          } = file;
                          const removeAttachmentLink = (
                            <a
                              id={file.fileName + index + '.removeAction'}
                              className="remove-attachment"
                              onClick={() => this.setDeleteItem(resourceId)}
                              title={`Remove the ${fileName} attachment`}
                              href="#"
                            >
                              <Icon link name="close" color="blue" />
                              Delete
                            </a>
                          );
                          const date = convertUTCDateToLocalDateDisplay(uploadedDate);
                          const linkWidth = hideUploadedBy ? 4 : 3;
                          return (
                            <FemaGoTable.Row
                              key={currentlyCollaborating ? attachmentId : index}
                            >
                              <FemaGoTable.TextCell width={linkWidth}>
                                <Icon name="check circle" color="green" />
                                {this.downloadAttachmentLink(resourceId, fileName)}
                              </FemaGoTable.TextCell>
                              <FemaGoTable.TextCell width={1}>
                                {date}
                              </FemaGoTable.TextCell>
                              {!hideUploadedBy ? (
                                <FemaGoTable.TextCell width={1}>
                                  {uploadedBy}
                                </FemaGoTable.TextCell>
                              ) : (
                                ''
                              )}
                              <FemaGoTable.TextCell width={1}>
                                {this.handleFileSize(fileSize)}
                              </FemaGoTable.TextCell>
                              <FemaGoTable.TextAreaCell
                                width={4}
                                rows={2}
                                value={description}
                                maxLength={501}
                                id={
                                  _.replace(file.fileName, /  */g, '_') +
                                  index +
                                  '.description'
                                }
                                name="description"
                                handleChange={(e, res) => {
                                  this.handleDescriptionChange(index, res);
                                }}
                                handleBlur={() => {
                                  this.props.updateDescription(
                                    FileTransferUtils.createPayload(
                                      this.state.filesSuccessful[index]
                                    )
                                  );
                                }}
                              />
                              <FemaGoTable.TextCell width={2}>
                                {removeAttachmentLink}
                              </FemaGoTable.TextCell>
                            </FemaGoTable.Row>
                          );
                        })}
                      </FemaGoTable.Body>
                    </FemaGoTable>
                  </Grid.Column>
                </Grid.Row>
              )}
              <Grid.Row>
                {!(extended_attachment && noFileDesc) && (
                  <Grid.Column width={6} className="left floated left aligned">
                    <strong>
                      <p>Maximum File Size:</p>
                    </strong>
                    {this.props.fileSizeLimit
                      ? convertFileSize(safeMult(this.props.fileSizeLimit, 1048576), 1024)
                      : '1 GB'}
                  </Grid.Column>
                )}
                {showAcceptedFileTypes && (
                  <Grid.Column width={6} className="right floated right aligned">
                    <strong>
                      <p>Accepted File Types:</p>
                    </strong>
                    {acceptedTypesDescriptor}
                  </Grid.Column>
                )}
              </Grid.Row>
            </Grid>
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default UploadPresentational;
